import { Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LegalPageLayout, {
  LegalSection as H2,
  LegalParagraph as P,
} from '@components/legal/LegalPageLayout';

const LAST_UPDATED = '27 October 2026';

/**
 * Data Processing Agreement (DRAFT). Public route /legal/dpa. Sets out the
 * controller/processor terms for customers subject to GDPR or similar laws.
 */
function DataProcessingAgreementPage() {
  const { t } = useTranslation();

  return (
    <LegalPageLayout title={t('legal.dpa')} lastUpdated={LAST_UPDATED}>
      <P>
        This Data Processing Agreement (&ldquo;DPA&rdquo;) forms part of the agreement between the
        customer (&ldquo;Controller&rdquo;) and AFixt (&ldquo;Processor,&rdquo; &ldquo;we&rdquo;)
        for use of the accessibility-testing platform (the &ldquo;Service&rdquo;). It governs our
        processing of personal data on the Controller&rsquo;s behalf and supplements our{' '}
        <Link component={RouterLink} to="/legal/privacy">
          {t('legal.privacy')}
        </Link>
        .
      </P>

      <H2>1. Roles</H2>
      <P>
        The Controller determines the purposes and means of processing Customer Data. We act as
        Processor, processing personal data only on the Controller&rsquo;s documented instructions,
        including those set out in the Service agreement and this DPA.
      </P>

      <H2>2. Subject matter, duration, nature, and purpose</H2>
      <P>
        We process personal data for the duration of the Service agreement to provide automated and
        human-assisted accessibility testing, conformance tracking, reporting, and attestation, as
        configured by the Controller.
      </P>

      <H2>3. Types of data and data subjects</H2>
      <P>
        Personal data processed may include account data of the Controller&rsquo;s users and
        reviewers, and any personal data incidentally present in HTML snapshots or screenshots of
        the properties the Controller chooses to test. Data subjects may include the
        Controller&rsquo;s personnel and visitors to the tested properties.
      </P>

      <H2>4. Processor obligations</H2>
      <P>
        We will: (a) process personal data only on documented instructions; (b) ensure personnel
        authorized to process data are bound by confidentiality; (c) implement the security measures
        described below; (d) assist the Controller with data-subject requests and with its own
        compliance obligations, taking into account the nature of processing; and (e) make available
        information reasonably necessary to demonstrate compliance.
      </P>

      <H2>5. Subprocessors</H2>
      <P>
        The Controller authorizes us to engage subprocessors to support the Service. We impose
        data-protection obligations on subprocessors no less protective than this DPA and remain
        responsible for their performance. We will provide notice of changes to subprocessors and an
        opportunity to object on reasonable grounds. Where AFixt&rsquo;s own AI key is used, our
        subprocessors include Anthropic, PBC, the AI model provider for the optional AI features;
        with an organization&rsquo;s own key, Anthropic acts under that organization&rsquo;s
        agreement with it. The current{' '}
        <Link component={RouterLink} to="/legal/subprocessors">
          subprocessor list
        </Link>{' '}
        is published.
      </P>

      <H2>6. Security measures</H2>
      <P>
        We maintain technical and organizational measures appropriate to the risk, including
        encryption of stored HTML snapshots and screenshots, encryption in transit, role-based
        access control, database-layer tenant isolation, and secret hygiene that keeps credentials
        and key material out of logs and API responses.
      </P>

      <H2>7. Personal data breaches</H2>
      <P>
        We will notify the Controller without undue delay after becoming aware of a personal data
        breach affecting Customer Data, and will provide information reasonably available to assist
        the Controller&rsquo;s own notification obligations.
      </P>

      <H2>8. Data-subject requests</H2>
      <P>
        Taking into account the nature of processing, we will assist the Controller by appropriate
        measures in responding to requests to exercise data-subject rights, including access,
        correction, deletion, restriction, and portability.
      </P>

      <H2>9. Return and deletion</H2>
      <P>
        On termination of the Service, the Controller may export Customer Data for a reasonable
        period, after which we delete or return it in accordance with the applicable retention
        policies, save where storage is required by law. Locked conformance snapshots are immutable
        and retained as evidence for the life of the relevant audit record.
      </P>

      <H2>10. Audits</H2>
      <P>
        We will make available information necessary to demonstrate compliance with this DPA and
        allow for and contribute to audits, including inspections, conducted by the Controller or an
        auditor it mandates, subject to reasonable confidentiality and scheduling terms.
      </P>

      <H2>11. International transfers</H2>
      <P>
        Where processing involves transfers of personal data across borders, the parties will rely
        on a lawful transfer mechanism such as the Standard Contractual Clauses, which are
        incorporated by reference where applicable. [Hosting regions and transfer mechanisms to be
        confirmed by counsel.]
      </P>

      <H2>12. Contact</H2>
      <P>
        Data-protection enquiries under this DPA can be sent to{' '}
        <Link href="mailto:privacy@afixt.com">privacy@afixt.com</Link>.
      </P>
    </LegalPageLayout>
  );
}

export default DataProcessingAgreementPage;
